// ============================================================================
// OMS Load shift API — move same-city backorders off a heavy delivery day onto
// an earlier one, then run the P2P sweep so they ship direct from the supplier.
// Backend: oms/api/views/load_shift.py.
// ============================================================================
import axios from "axios";
import { errorActions } from "../../../helpers/auth_helper";

export type LoadShiftBlocker =
  | "vendor_not_ready"
  | "payment_pending"
  | "customer_date"
  | "shares_truck";

export interface LoadShiftRow {
  allocation_id: number;
  po_number: string | null;
  sales_order_id: number;
  so_number: string | null;
  supplier_name: string | null;
  supplier_warehouse: string | null;
  supplier_city: string | null; // supplier warehouse's address city (free text)
  city: string | null; // hub city the supplier resolves to
  customer_hub_city: string | null; // hub city the customer resolves to — equals `city` for every row
  customer_name: string | null;
  customer_city: string | null; // ship-to address city (free text, often a locality)
  customer_pincode: string | null;
  promised_availability_date: string | null;
  target_delivery_date: string | null;
  item_count: number;
  is_express: boolean;
  is_unloading: boolean; // needs unloading labour at the customer site
  truck_booked: boolean; // a prepone cancels and re-books this truck
  travels_with: string[]; // PO numbers on the same truck — a prepone moves them too
  blockers: LoadShiftBlocker[];
  selectable: boolean;
}

export interface LoadShiftCandidates {
  scheduled: string;
  prepone_to: string;
  rows: LoadShiftRow[];
  selectable: number;
}

export type PreponeOutcome =
  | "preponed"
  | "moved_with_truck"
  | "blocked"
  | "not_candidate"
  | "refused"
  | "failed";

export interface PreponeResult {
  allocation_id: number;
  po_number: string | null;
  outcome: PreponeOutcome;
  detail: string | null;
}

export type SweepOutcome = "upgraded" | "skipped" | "manual" | "failed";

export interface SweepSummary {
  due_date: string | null;
  candidates: number;
  not_candidates: number[];
  upgraded: number;
  skipped: number;
  manual: number;
  failed: number;
  allocations: number[];
  manual_allocations: number[];
  outcomes: Record<string, SweepOutcome>;
  rows?: LoadShiftRow[]; // dry run only: one row per candidate
}

// The backend caps a live sweep request at this many allocations (MAX_SWEEP_CHUNK).
export const SWEEP_CHUNK = 5;

export async function loadShiftCandidates(
  scheduled: string,
  prepone_to: string
): Promise<LoadShiftCandidates | undefined> {
  return axios
    .get("/oms/load-shift/candidates/", { params: { scheduled, prepone_to } })
    .then((res) => res?.data)
    .catch((error) => {
      errorActions(error, "Error loading orders");
      return undefined;
    });
}

export async function preponeBatch(body: {
  scheduled: string;
  prepone_to: string;
  allocation_ids: number[];
  reason?: string;
}): Promise<PreponeResult[] | undefined> {
  return axios
    .post("/oms/load-shift/prepone/", body)
    .then((res) => res?.data?.results)
    .catch((error) => {
      errorActions(error, "Prepone failed");
      return undefined;
    });
}

export async function runSweep(body: {
  due_date: string;
  dry_run?: boolean;
  allocation_ids?: number[];
}): Promise<SweepSummary | undefined> {
  return axios
    .post("/oms/load-shift/sweep/", body)
    .then((res) => res?.data)
    .catch((error) => {
      errorActions(error, "P2P sweep failed");
      return undefined;
    });
}
